import { useState } from "react";
import Button from "@/components/base/Button";
import AdminPageHeader from "@/pages/admin/components/AdminPageHeader";
import EmptyState from "@/pages/admin/components/EmptyState";
import ResourceForm from "@/pages/admin/components/ResourceForm";
import { useTableData } from "@/hooks/useTableData";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/lib/supabase";
import { formatFecha } from "@/lib/format";
import type { FieldConfig } from "@/lib/adminFields";
import type { AdminUser } from "@/types";

const rolLabels: Record<string, string> = {
  admin: "Administrador",
  editor: "Editor",
};

const fields: FieldConfig[] = [
  { name: "nombre", label: "Nombre", type: "text", required: true },
  {
    name: "rol",
    label: "Rol",
    type: "select",
    options: [
      { value: "admin", label: "Administrador (acceso total)" },
      { value: "editor", label: "Editor" },
    ],
  },
  { name: "activo", label: "Acceso permitido", type: "switch" },
];

export default function AdminUsuarios() {
  const { user } = useAuth();
  const { rows, loading, error, reload } = useTableData<AdminUser>("admin_users", "creado_en");
  const [editing, setEditing] = useState<AdminUser | null>(null);
  const [confirmRow, setConfirmRow] = useState<AdminUser | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState("");

  const handleSave = async (values: Record<string, unknown>) => {
    if (!editing) return;
    const { error: saveError } = await supabase
      .from("admin_users")
      .update({
        nombre: String(values.nombre ?? ""),
        rol: String(values.rol ?? "admin"),
        activo: Boolean(values.activo),
        actualizado_en: new Date().toISOString(),
      })
      .eq("id", editing.id);
    if (saveError) throw saveError;
    setEditing(null);
    await reload();
  };

  const handleToggle = async (row: AdminUser) => {
    setBusyId(row.id);
    setActionError("");
    try {
      const { error: toggleError } = await supabase
        .from("admin_users")
        .update({ activo: !row.activo, actualizado_en: new Date().toISOString() })
        .eq("id", row.id);
      if (toggleError) throw toggleError;
      await reload();
    } catch {
      setActionError("No pudimos cambiar el acceso. Intenta nuevamente.");
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async () => {
    if (!confirmRow) return;
    if (confirmRow.id === user?.id) {
      setActionError("No puedes eliminar tu propia cuenta de administrador.");
      setConfirmRow(null);
      return;
    }
    setBusyId(confirmRow.id);
    setActionError("");
    try {
      const { error: deleteError } = await supabase.from("admin_users").delete().eq("id", confirmRow.id);
      if (deleteError) throw deleteError;
      await reload();
    } catch {
      setActionError("No pudimos eliminar al administrador. Intenta nuevamente.");
    } finally {
      setBusyId(null);
      setConfirmRow(null);
    }
  };

  return (
    <div>
      <AdminPageHeader
        title="Usuarios administradores"
        description="Gestiona quién puede acceder al panel, su rol y si su acceso está activo."
        action={
          <Button variant="outline" size="md" icon="ri-refresh-line" onClick={reload} disabled={loading}>
            Actualizar
          </Button>
        }
      />

      <div className="mb-4 flex items-start gap-2 rounded-lg border border-secondary-200 bg-secondary-50 px-4 py-3 text-sm text-secondary-900">
        <i className="ri-information-line mt-0.5 text-base leading-none" aria-hidden="true"></i>
        <span>
          El primer usuario que se registra se convierte en administrador automáticamente. Los demás accesos se
          conceden aquí una vez que la persona crea su cuenta.
        </span>
      </div>

      {actionError && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-primary-800">
          <i className="ri-error-warning-line mt-0.5 text-base leading-none" aria-hidden="true"></i>
          {actionError}
        </div>
      )}

      {error ? (
        <div className="flex flex-col items-center rounded-lg border border-primary-200 bg-primary-50 px-6 py-10 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 text-primary-700">
            <i className="ri-wifi-off-line text-2xl leading-none" aria-hidden="true"></i>
          </span>
          <h3 className="mt-4 text-base font-heading font-semibold text-foreground-950">No se pudo cargar la lista</h3>
          <p className="mt-1.5 max-w-md text-sm text-foreground-600">{error}</p>
          <Button variant="primary" size="md" icon="ri-refresh-line" onClick={reload} className="mt-5">
            Reintentar
          </Button>
        </div>
      ) : loading ? (
        <div className="space-y-3">
          {[0, 1, 2].map((key) => (
            <div key={key} className="h-16 animate-pulse rounded-lg border border-background-200 bg-background-100"></div>
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon="ri-team-line"
          title="Todavía no hay administradores"
          description="Cuando se registre el primer usuario, aparecerá aquí como administrador."
        />
      ) : (
        <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-left">
              <thead>
                <tr className="border-b border-background-200 bg-background-100 text-xs font-label uppercase tracking-wide text-foreground-500">
                  <th className="px-4 py-3 font-semibold">Administrador</th>
                  <th className="px-4 py-3 font-semibold">Rol</th>
                  <th className="px-4 py-3 font-semibold">Alta</th>
                  <th className="px-4 py-3 font-semibold">Acceso</th>
                  <th className="px-4 py-3 text-right font-semibold">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-200">
                {rows.map((row) => (
                  <tr key={row.id} className="transition-colors hover:bg-background-100">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-500 text-sm font-label font-semibold text-background-50 dark:text-foreground-950">
                          {(row.nombre || row.email || "A").charAt(0).toUpperCase()}
                        </span>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-foreground-900">{row.nombre || "Sin nombre"}</p>
                          <p className="truncate text-xs text-foreground-500">{row.email || "—"}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground-600">{rolLabels[row.rol] ?? row.rol}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-foreground-500">{formatFecha(row.creado_en)}</td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => void handleToggle(row)}
                        disabled={busyId === row.id}
                        className={`inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 text-xs font-label font-medium transition-colors disabled:opacity-50 ${
                          row.activo
                            ? "border-primary-200 bg-primary-50 text-primary-800"
                            : "border-background-300 bg-background-100 text-foreground-500"
                        }`}
                      >
                        <span className={`h-1.5 w-1.5 rounded-full ${row.activo ? "bg-primary-500" : "bg-foreground-400"}`} aria-hidden="true"></span>
                        {row.activo ? "Activo" : "Bloqueado"}
                      </button>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => setEditing(row)}
                        aria-label="Editar administrador"
                        className="mr-1 inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700"
                      >
                        <i className="ri-pencil-line text-base leading-none" aria-hidden="true"></i>
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmRow(row)}
                        disabled={row.id === user?.id}
                        aria-label="Eliminar administrador"
                        className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        <i className="ri-delete-bin-6-line text-base leading-none" aria-hidden="true"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-foreground-950/40 p-4 md:p-8">
          <div className="w-full max-w-lg rounded-lg border border-background-200 bg-background-50">
            <div className="flex items-center justify-between border-b border-background-200 px-5 py-4">
              <h2 className="text-base font-heading font-semibold text-foreground-950">Editar administrador</h2>
              <button
                type="button"
                onClick={() => setEditing(null)}
                aria-label="Cerrar"
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-background-100 hover:text-foreground-900"
              >
                <i className="ri-close-line text-xl leading-none" aria-hidden="true"></i>
              </button>
            </div>
            <div className="p-5">
              <ResourceForm
                fields={fields}
                initial={editing as unknown as Record<string, unknown>}
                submitLabel="Guardar cambios"
                onSubmit={handleSave}
                onCancel={() => setEditing(null)}
              />
            </div>
          </div>
        </div>
      )}

      {confirmRow && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground-950/40 p-4">
          <div className="w-full max-w-sm rounded-lg border border-background-200 bg-background-50 p-6 text-center">
            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 text-primary-700">
              <i className="ri-user-unfollow-line text-2xl leading-none" aria-hidden="true"></i>
            </span>
            <h3 className="mt-4 text-base font-heading font-semibold text-foreground-950">¿Quitar el acceso?</h3>
            <p className="mt-1.5 text-sm text-foreground-600">
              {confirmRow.nombre || confirmRow.email} dejará de poder entrar al panel.
            </p>
            <div className="mt-5 flex gap-3">
              <button
                type="button"
                onClick={() => setConfirmRow(null)}
                className="flex-1 cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm font-label font-semibold text-foreground-700 transition-colors hover:bg-background-100"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => void handleDelete()}
                disabled={busyId === confirmRow.id}
                className="flex-1 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-label font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-50 dark:text-foreground-950"
              >
                {busyId === confirmRow.id ? "Quitando…" : "Quitar acceso"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}